/**
 * The suppression window that keeps a super-admin's impersonation exit from
 * looking like an expired session.
 *
 * `/admin/impersonation/end` now REVOKES the impersonation session rather than
 * only writing an audit row, so every TanStack refetch still in flight 401s the
 * moment it returns. `lib/api/client.ts` answers a 401 by clearing tokens and
 * navigating to `unauthorizedRedirectTarget(...)`, and the customer shell is
 * not in its exempt-path list — so without this the operator lands on the
 * CUSTOMER /login page instead of the /admin destination the banner is already
 * navigating to.
 *
 * A STAMPED DEADLINE, not a boolean. Setting a flag and clearing it on the line
 * after `window.location.href = …` opens a zero-width window: assigning `href`
 * does not stop JavaScript, so the clear runs immediately and the race is still
 * lost. Never clearing it is worse — a genuine expiry in that tab afterwards
 * would stop redirecting to login, which is a security control going quiet. A
 * deadline is open long enough and closes on its own.
 *
 * Lives in its own module so the banner (writer) and the API client (reader)
 * share one definition of the key and the window, instead of two string
 * literals that can drift apart.
 */
export const IMPERSONATION_EXIT_KEY = 'hf-app:impersonation-exiting';

/**
 * How long the exit teardown is allowed to suppress the force-logout.
 *
 * Long enough to cover the revoke, the cancelled queries, the stragglers that
 * 401 behind them and the navigation; short enough that a tab left open after a
 * failed exit is back to normal expiry handling well inside a working session.
 */
export const IMPERSONATION_EXIT_WINDOW_MS = 30_000;

/** Open the window. Called by the banner BEFORE it calls /end. */
export function markImpersonationExiting(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(IMPERSONATION_EXIT_KEY, String(Date.now()));
  } catch {
    // Private mode / blocked storage: the suppression is a nicety, never a
    // correctness requirement. Losing it costs a wrong redirect, not a session.
  }
}

/** Is an impersonation exit tearing down right now? */
export function isImpersonationExiting(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = sessionStorage.getItem(IMPERSONATION_EXIT_KEY);
    if (!raw) return false;
    const startedAt = Number(raw);
    // Anything unreadable — including the legacy literal 'true' this replaced —
    // is treated as CLOSED. Failing open here would suppress the login redirect
    // for the life of the tab.
    if (!Number.isFinite(startedAt)) return false;
    return Date.now() - startedAt < IMPERSONATION_EXIT_WINDOW_MS;
  } catch {
    return false;
  }
}

/**
 * Close the window early. Only for an exit that ABANDONED the teardown without
 * navigating — a successful exit leaves the deadline to expire on its own,
 * because the clear would otherwise run before the navigation it is protecting.
 */
export function clearImpersonationExiting(): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.removeItem(IMPERSONATION_EXIT_KEY);
  } catch {
    // See markImpersonationExiting.
  }
}
